/**
 * Trees API - Cliente HTTP para gestión de arbolado urbano
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/map-tools
 */

import axios from 'axios';
import type { TreeComplete, Coordinates } from './tree-types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * DTO para crear un árbol
 */
export interface CreateTreeDto {
  projectId: string;
  coordinates: Coordinates;
  code?: string;
  data?: Record<string, any>;
}

/**
 * DTO para actualizar un árbol
 */
export interface UpdateTreeDto {
  code?: string;
  coordinates?: Coordinates;
  data?: Record<string, any>;
  status?: string;
}

/**
 * Respuesta del backend para un árbol
 */
export interface TreeResponse {
  id: string;
  project_id: string;
  code: string;
  coordinates: Coordinates;
  data: Record<string, any>;
  status: string;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Cliente API para árboles
 */
export const treesApi = {
  /**
   * Crear un nuevo árbol
   *
   * @param tree - Datos del árbol (solo requiere projectId y coordenadas)
   * @returns Árbol creado
   *
   * @example
   * ```typescript
   * const newTree = await treesApi.create({
   *   projectId: 'uuid-del-proyecto',
   *   coordinates: [-68.5375, -31.5383]
   * });
   * ```
   */
  async create(tree: CreateTreeDto): Promise<TreeResponse> {
    const token = getAuthToken();
    const response = await axios.post<TreeResponse>(
      `${API_URL}/trees`,
      tree,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Obtener todos los árboles de un proyecto
   *
   * @param projectId - ID del proyecto
   * @returns Lista de árboles
   */
  async getByProject(projectId: string): Promise<TreeResponse[]> {
    const token = getAuthToken();
    const response = await axios.get<TreeResponse[]>(
      `${API_URL}/trees/project/${projectId}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Obtener un árbol por ID
   *
   * @param id - ID del árbol
   * @returns Árbol encontrado
   */
  async get(id: string): Promise<TreeResponse> {
    const token = getAuthToken();
    const response = await axios.get<TreeResponse>(
      `${API_URL}/trees/${id}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Actualizar un árbol
   *
   * @param id - ID del árbol
   * @param updates - Campos a actualizar
   * @returns Árbol actualizado
   */
  async update(id: string, updates: UpdateTreeDto): Promise<TreeResponse> {
    const token = getAuthToken();
    const response = await axios.put<TreeResponse>(
      `${API_URL}/trees/${id}`,
      updates,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Eliminar un árbol (soft delete)
   *
   * @param id - ID del árbol
   * @returns Árbol eliminado
   */
  async delete(id: string): Promise<TreeResponse> {
    const token = getAuthToken();
    const response = await axios.delete<TreeResponse>(
      `${API_URL}/trees/${id}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
};

/**
 * Convierte TreeComplete (frontend) a CreateTreeDto (backend)
 *
 * @param tree - Árbol del frontend
 * @param projectId - ID del proyecto
 * @returns DTO para el backend
 *
 * @remarks
 * Si el árbol tiene un código temporal (TEMP-TREE-*), no se envía al backend
 * para permitir que el sistema de codificación geoespacial genere el código
 * definitivo basado en la ubicación del árbol.
 */
export function treeToCreateDto(tree: TreeComplete, projectId: string): CreateTreeDto {
  // No enviar códigos temporales al backend
  const isTemporaryCode = tree.code?.startsWith('TEMP-') ?? false;

  return {
    projectId,
    coordinates: tree.coordinates,
    code: isTemporaryCode ? undefined : tree.code,
    data: {
      // General
      commonName: tree.commonName,
      scientificName: tree.scientificName,
      botanicalFamily: tree.botanicalFamily,
      origin: tree.origin,
      plantingDate: tree.plantingDate?.toISOString(),
      estimatedAge: tree.estimatedAge,
      address: tree.address,
      betweenStreets: tree.betweenStreets,
      nearestNumber: tree.nearestNumber,
      neighborhood: tree.neighborhood,
      locationType: tree.locationType,

      // Health
      dbh: tree.dbh,
      totalHeight: tree.totalHeight,
      firstBranchHeight: tree.firstBranchHeight,
      crownDiameter: tree.crownDiameter,
      crownShape: tree.crownShape,
      generalCondition: tree.generalCondition,
      rootsCondition: tree.rootsCondition,
      trunkCondition: tree.trunkCondition,
      branchesCondition: tree.branchesCondition,
      foliageCondition: tree.foliageCondition,
      inclination: tree.inclination,
      pests: tree.pests,
      diseases: tree.diseases,
      hasCavities: tree.hasCavities,
      cavitiesDescription: tree.cavitiesDescription,

      // Risk
      overallRiskLevel: tree.overallRiskLevel,
      wiringInterference: tree.wiringInterference,
      signageInterference: tree.signageInterference,
      buildingInterference: tree.buildingInterference,
      pavementDamage: tree.pavementDamage,
      trafficObstruction: tree.trafficObstruction,
      nearSensitiveAreas: tree.nearSensitiveAreas,
      sensitiveAreasDescription: tree.sensitiveAreasDescription,
      surroundingSoilType: tree.surroundingSoilType,
      hasTreeGrate: tree.hasTreeGrate,
      grateLength: tree.grateLength,
      grateWidth: tree.grateWidth,
      rootSpace: tree.rootSpace,
      crownSpace: tree.crownSpace,
      hasStakes: tree.hasStakes,
      stakesType: tree.stakesType,
      streetLightDistance: tree.streetLightDistance,
      pedestrianTraffic: tree.pedestrianTraffic,
      vehicularTraffic: tree.vehicularTraffic,

      // Maintenance
      needsIntervention: tree.needsIntervention,
      interventionType: tree.interventionType,
      priority: tree.priority,
      scheduledIntervention: tree.scheduledIntervention?.toISOString(),
      assignedResponsible: tree.assignedResponsible,
      lastPruningDate: tree.lastPruningDate?.toISOString(),
      lastPruningType: tree.lastPruningType,
      lastFertilizationDate: tree.lastFertilizationDate?.toISOString(),
      lastFertilizationProduct: tree.lastFertilizationProduct,
      lastIrrigationDate: tree.lastIrrigationDate?.toISOString(),
      lastTreatmentDate: tree.lastTreatmentDate?.toISOString(),
      lastTreatmentProduct: tree.lastTreatmentProduct,
      lastTreatmentTarget: tree.lastTreatmentTarget,
      treatmentHistory: tree.treatmentHistory,

      // Heritage
      ornamentalValue: tree.ornamentalValue,
      ecologicalValue: tree.ecologicalValue,
      hasHistoricalValue: tree.hasHistoricalValue,
      historicalDescription: tree.historicalDescription,
      isProtected: tree.isProtected,
      protectionLaw: tree.protectionLaw,
      isEndangeredSpecies: tree.isEndangeredSpecies,
      ecosystemServices: tree.ecosystemServices,
      foliageType: tree.foliageType,
      floweringSeason: tree.floweringSeason,
      fruitingSeason: tree.fruitingSeason,
      growthRate: tree.growthRate,
      waterRequirements: tree.waterRequirements,
      owner: tree.owner,
      managementArea: tree.managementArea,
      registryStatus: tree.registryStatus,
      generalNotes: tree.generalNotes,
    },
  };
}
